import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/lib/AuthContext";
import { getErrorMessage } from "@/lib/utils";
import BrandLogo from "@/components/BrandLogo";
import ReCAPTCHA from "react-google-recaptcha";

export default function SignupPage() {
  const navigate = useNavigate();
  const { signup } = useAuth();
  const [form, setForm] = useState({ email: "", password: "", full_name: "", recaptcha_token: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.recaptcha_token) {
      setError("Please complete the reCAPTCHA challenge");
      return;
    }
    setError("");
    setLoading(true);
    try {
      await signup(form);
      navigate("/dashboard");
    } catch (err) {
      setError(getErrorMessage(err, "Registration failed"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC] p-8" data-testid="signup-page">
      <div className="w-full max-w-sm bg-white rounded-lg border border-slate-200 p-8 space-y-6">
        <BrandLogo
          className="justify-center"
          iconClassName="w-9 h-9 rounded-lg"
          textClassName="text-xl font-extrabold tracking-tight font-heading text-slate-900"
        />
        <div className="text-center">
          <h1 className="text-2xl font-black tracking-tight font-heading text-slate-900">Create Account</h1>
          <p className="text-sm text-slate-500 mt-1">Join your team's rental desk</p>
        </div>
        {error && <div data-testid="signup-error" className="bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3 rounded-md">{error}</div>}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1.5 block">Full Name</label>
            <input data-testid="signup-name" required className="w-full h-12 px-4 text-sm border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black/10 focus:border-black" value={form.full_name} onChange={e => setForm(p => ({ ...p, full_name: e.target.value }))} />
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1.5 block">Email</label>
            <input data-testid="signup-email" type="email" required className="w-full h-12 px-4 text-sm border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black/10 focus:border-black" value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} />
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1.5 block">Password</label>
            <input data-testid="signup-password" type="password" required className="w-full h-12 px-4 text-sm border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black/10 focus:border-black" placeholder="Min 8 chars, 1 uppercase, 1 digit" value={form.password} onChange={e => setForm(p => ({ ...p, password: e.target.value }))} />
          </div>

          <div className="flex justify-center py-2">
            <ReCAPTCHA
              sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY || "6Lf0RbYsAAAAAJH3kxFpusRPTQUgJaID4tAQXvNm"}
              onChange={token => setForm(p => ({ ...p, recaptcha_token: token }))}
            />
          </div>

          <button data-testid="signup-submit" type="submit" disabled={loading} className="w-full h-12 bg-primary text-primary-foreground rounded-md text-sm font-bold hover:bg-primary/90 disabled:opacity-50 transition-colors">
            {loading ? "Creating..." : "Create Account"}
          </button>
        </form>
        <p className="text-sm text-slate-500 text-center">
          Already have an account? <Link to="/login" className="text-foreground font-bold hover:underline">Sign in</Link>
        </p>
      </div>
    </div>
  );
}
